/**
 * Secondary Navigation Component
 *
 * scroll-spy functionality to highlight the current section.
 * Includes both mobile (collapsible) and desktop layouts.
 *
 * @component SecondaryNav
 * @extends HTMLElement
 */
function doSecondaryNav() {
  if (customElements.get('secondary-nav')) return

  class SecondaryNav extends HTMLElement {
    constructor() {
      super()
      this.sections = []
      this.navItems = []
      this.lastScrollTop = 0
      this.header = document.querySelector('header')
      this.announcementBar = document.querySelector('.announcement-bar-section')
      this.sectionElement = this.closest('.secondary-nav')
      this.handleResizeOrPosition = this.handleResizeOrPosition.bind(this)
      this.jumpNavTrigger = this.querySelector('.jump-nav__trigger')
      this.init()
    }

    init() {
      // Get all nav items and their corresponding sections
      this.navItems = Array.from(this.querySelectorAll('.jump-nav__item'))
      this.hideNavItemIfSectionDoesNotExist(
        Array.from(this.querySelectorAll('.jump-nav__item'))
      )
      this.navLinksDesktop = Array.from(
        this.querySelectorAll('.jump-nav__menu-desktop .jump-nav__link')
      )
      this.navLinksMobile = Array.from(
        this.querySelectorAll('.jump-nav__menu .jump-nav__link')
      )

      // Ensure first mobile nav link is active on load if none are
      if (
        this.navLinksMobile.length > 0 &&
        !this.navLinksMobile.some((link) => link.classList.contains('active'))
      ) {
        this.navLinksMobile[0].classList.add('active')
      }

      this.sections = this.navLinksDesktop
        .map((item) => {
          const href = item.getAttribute('href')
          if (href.startsWith('#')) {
            const sectionId = href.substring(1)
            return document.getElementById(sectionId)
          }
          return null
        })
        .filter(Boolean)

      this.scrollSpy(this.sections, this.navLinksDesktop)
      this.scrollSpy(this.sections, this.navLinksMobile)

      // Helper for robust scroll to top
      const scrollToTopLoop = () => {
        let attempts = 0
        function loop() {
          window.scrollTo({ top: 0, behavior: 'smooth' })
          attempts++
          if (window.scrollY > 0 && attempts < 10) {
            requestAnimationFrame(loop)
          }
        }
        loop()
      }

      // Intercept anchor clicks for smooth scroll with offset
      this.querySelectorAll('.jump-nav__link[href^="#"]').forEach((link) => {
        link.addEventListener('click', (e) => {
          const href = link.getAttribute('href')
          if (!href || href.length < 2) return
          const targetId = href.substring(1)
          const target = document.getElementById(targetId)
          if (!target) return
          e.preventDefault()

          // Check if parent li has data-top-top='true'
          const parentItem = link.closest('.jump-nav__item')
          if (
            parentItem &&
            parentItem.getAttribute('data-top-top') === 'true'
          ) {
            scrollToTopLoop()
            return
          }

          // Calculate offset: announcement bar + secondary nav
          let offset = 0
          if (this.announcementBar && this.announcementBar.offsetHeight > 0) {
            offset += this.announcementBar.offsetHeight
          }
          if (this.sectionElement && this.sectionElement.offsetHeight > 0) {
            offset += this.sectionElement.offsetHeight
          }

          // Get the top position of the target
          const targetRect = target.getBoundingClientRect()
          const scrollTop =
            window.pageYOffset || document.documentElement.scrollTop
          const targetTop = targetRect.top + scrollTop - offset

          window.scrollTo({
            top: targetTop,
            behavior: 'smooth',
          })

          this.jumpNavTrigger.click()
        })
      })

      // Set initial position (below header and announcement bar)
      this.handleResizeOrPosition()
      window.addEventListener('resize', this.handleResizeOrPosition)
      if (this.announcementBar) {
        // Use ResizeObserver instead of MutationObserver for height changes
        this.announcementBarResizeObserver = new ResizeObserver(() => {
          this.updatePosition()
        })
        this.announcementBarResizeObserver.observe(this.announcementBar)
      }

      // Log section height and next section element
      this.logSectionInfo()

      // Bind scroll event
      this.onScroll = this.onScroll.bind(this)
      window.addEventListener('scroll', this.onScroll, { passive: true })

      // Initial positioning
      this.updatePosition()

      // Initial check
      this.onScroll()
    }

    hideNavItemIfSectionDoesNotExist(navItems) {
      navItems.forEach((item) => {
        const customScrollToId = item.dataset.customSectionId
        const customScrollToIdElement =
          document.getElementById(customScrollToId)
        if (!customScrollToIdElement) {
          item.style.display = 'none'
        }
      })
    }

    handleResizeOrPosition() {
      if (!this.sectionElement) return
      let offset = 0
      if (this.announcementBar && this.announcementBar.offsetHeight > 0) {
        offset += this.announcementBar.offsetHeight
      }
      if (this.header && this.header.offsetHeight > 0) {
        offset += this.header.offsetHeight
      }
      this.sectionElement.style.top = `${offset}px`
    }

    logSectionInfo() {
      // Wait for DOM to be fully rendered
      requestAnimationFrame(() => {
        if (this.sectionElement) {
          // Get the section height
          const sectionHeight = this.sectionElement.offsetHeight

          // Get the next section element
          const nextSection = this.sectionElement.nextElementSibling

          if (nextSection) {
            // Get current padding of next section
            const computedStyle = window.getComputedStyle(nextSection)
            const currentPadding = parseFloat(computedStyle.paddingTop) || 0

            // Add section height to the next section's padding-top
            const newPadding = currentPadding + sectionHeight
            nextSection.style.paddingTop = `${newPadding}px`
          }
        }
      })
    }

    updatePosition() {
      if (!this.sectionElement) return

      let announcementBarHeight = 0
      if (this.announcementBar && this.announcementBar.offsetHeight > 0) {
        announcementBarHeight = this.announcementBar.offsetHeight
      }

      let headerHeight = 0
      let headerIsVisible = true
      if (this.header) {
        // Check for hidden class (adjust class name if your theme uses a different one)
        headerIsVisible = !this.header.classList.contains(
          'shopify-section-header-hidden'
        )
        if (headerIsVisible && this.header.offsetHeight > 0) {
          headerHeight = this.header.offsetHeight
        }
      }

      const currentScrollTop =
        window.pageYOffset || document.documentElement.scrollTop
      const isScrollingDown = currentScrollTop > this.lastScrollTop

      if (isScrollingDown) {
        // When scrolling down, stick below announcement bar (if present)
        this.sectionElement.style.top = `${announcementBarHeight}px`
      } else {
        // When scrolling up, position below header and announcement bar, but only if header is visible
        this.sectionElement.style.top = `${announcementBarHeight + (headerIsVisible ? headerHeight : 0)}px`
      }

      this.lastScrollTop = currentScrollTop <= 0 ? 0 : currentScrollTop
    }

    onScroll() {
      const viewportHeight = window.innerHeight

      // Update the positioning
      this.updatePosition()

      // Add or remove 'is-scrolled' class based on scroll position
      if (window.scrollY > 0) {
        this.sectionElement.classList.add('is-scrolled')
      } else {
        this.sectionElement.classList.remove('is-scrolled')
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll)
      window.removeEventListener('resize', this.handleResizeOrPosition)
      if (this.announcementBarResizeObserver) {
        this.announcementBarResizeObserver.disconnect()
      }
    }

    scrollSpy(sections, menu_links) {
      const makeActive = (link) => menu_links[link].classList.add('active')
      const removeActive = (link) => menu_links[link].classList.remove('active')
      const removeAllActive = () =>
        [...Array(sections.length).keys()].forEach((link) => removeActive(link))
      const sectionMargin = 200
      let currentActive = 0

      // Extracted logic to set active nav item
      const updateActive = () => {
        const current =
          sections.length -
          [...sections]
            .reverse()
            .findIndex(
              (section) => window.scrollY >= section.offsetTop - sectionMargin
            ) -
          1
        if (
          current !== currentActive &&
          current >= 0 &&
          current < sections.length
        ) {
          removeAllActive()
          currentActive = current
          makeActive(current)
        }
      }

      window.addEventListener('scroll', updateActive)
      // Call once on initialization to set the correct active state
      updateActive()
    }
  }

  customElements.define('secondary-nav', SecondaryNav)
}

doSecondaryNav()
